import React, { useEffect, useState } from "react";
import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import { GraduationCap, MessageCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useValidationSchema } from "i18n/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { PATHS } from "router/config/paths";
import utilsService from "api/utils/utils.service";
import { useAppSelector } from "store";
import { httpStatusOf } from "utils/httpStatusOf";

const SupportCard = ({ title, text, Icon, button }: any) => (
  <div className="flex w-[340px] gap-5 border border-black-300 rounded-[5px]">
    <div className="flex flex-col bg-black rounded-[5px] flex-1">
      <div className="p-2 bg-body-100 text-h6 text-white rounded-t-[4px]">{title}</div>
      <div className="flex flex-1 flex-col gap-5 w-full p-3">
        {Icon}
        <span className="text-bodySm text-white">{text}</span>
      </div>
      <div className="flex bg-black-100 gap-2.5 p-2.5 border-t border-black-300 rounded-b-[4px]">{button}</div>
    </div>
  </div>
);

const CaseSubmitted = ({ onClose, show }: any) => {
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose} show={show} bodyClassName="!rounded-[5px] !border-none">
      <div className="flex flex-col bg-black w-[320px] border border-black-300 rounded-[5px]">
        <div className="p-2 bg-black-300 text-h6 text-white rounded-t-[4px]">{t("account:support.submitted.title")}</div>
        <div className="flex flex-col gap-3 w-full rounded-md p-3">
          <span className="text-bodySm text-white">{t("account:support.submitted.body")}</span>
        </div>
        <div className="flex bg-black-100 gap-2.5 p-2.5 border-t border-black-300 rounded-b-[4px]">
          <Button className="btn-secondary w-full" onClick={onClose}>
            {t("common:close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.common);
  const { t } = useTranslation();
  const schema = useValidationSchema((tFn) =>
    yup
      .object({
        feature: yup.string().required(tFn("validation:support.field_required")),
        subject: yup.string().required(tFn("validation:support.field_required")),
        description: yup.string().required(tFn("validation:support.field_required")),
      })
      .required()
  );
  const [showCaseSubmitted, setShowCaseSubmitted] = useState(false);
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [submitCaseDisabeld, setSubmitCaseDisabeld] = useState(false);

  // Deep-link: "Get in touch" from a feature modal lands here with the case form already open.
  useEffect(() => {
    if ((location.state as { openSupportCase?: boolean } | null)?.openSupportCase) setShowCaseForm(true);
  }, [location.state]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      feature: "",
      subject: "",
      description: "",
    },
  });

  const onValid = async (data: any) => {
    setSubmitCaseDisabeld(true);
    utilsService
      .contactUs({
        first_name: user.name ?? "",
        last_name: "",
        email: user.email ?? "",
        company: user.work ?? "",
        company_size: "",
        message: data.feature + " " + data.subject + " " + data.description,
        type: "account_support",
      })
      .then(() => {
        setShowCaseSubmitted(true);
        setSubmitCaseDisabeld(false);
      })
      .catch((e) => {
        console.log("Failed to submit the support case", { status: httpStatusOf(e) });
        setSubmitCaseDisabeld(false);
      });
  };

  const onHandleSubmit = () => {
    handleSubmit(
      (data) => onValid(data),
      // REF-6708: never the FieldErrors themselves — each entry holds the input's ref, and its value.
      () => console.log("Support form is invalid")
    )();
  };

  return (
    <div className="flex flex-col w-fit items-center mx-auto gap-10">
      <CaseSubmitted show={showCaseSubmitted} onClose={() => setShowCaseSubmitted(false)} />
      <h2 className="text-h2 text-white">{t("account:support.title")}</h2>
      <div className="flex justify-center gap-5 w-full">
        <SupportCard
          title={t("account:support.academy.title")}
          text={t("account:support.academy.text")}
          Icon={<GraduationCap className="w-8 h-8 text-blackGreen" />}
          button={
            <Button className="btn-primary-small w-full" onClick={() => navigate(PATHS.ACADEMY_HOME)}>
              {t("account:support.academy.btn")}
            </Button>
          }
        />
        <SupportCard
          title={t("account:support.customer.title")}
          text={t("account:support.customer.text")}
          Icon={<MessageCircle className="w-8 h-8 text-blackGreen" />}
          button={
            <Button className="btn-primary-small w-full" onClick={() => setShowCaseForm((prev) => !prev)}>
              {t("account:support.customer.btn")}
            </Button>
          }
        />
      </div>
      {showCaseForm && (
        <div className="flex flex-col bg-black w-full border border-black-300 rounded-[5px]">
          <div className="p-2 bg-body-100 text-h6 text-white rounded-t-[4px]">{t("account:support.case.title")}</div>
          <div className="flex flex-col gap-3 w-full rounded-md p-3">
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-white">{t("account:support.case.feature_label")}</span>
              <Input {...register("feature")} error={errors.feature?.message} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-white">{t("account:support.case.subject_label")}</span>
              <Input {...register("subject")} error={errors.subject?.message} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-white">{t("account:support.case.description_label")}</span>
              <Input {...register("description")} error={errors.description?.message} />
            </div>
          </div>
          <div className="flex bg-black-100 gap-2.5 p-2.5 justify-end border-t border-black-300 rounded-b-[5px]">
            <Button className="btn-secondary-small w-fit" onClick={() => setShowCaseForm(false)}>
              {t("common:cancel")}
            </Button>
            <Button className="btn-primary-small w-fit" disabled={submitCaseDisabeld} onClick={onHandleSubmit}>
              {t("account:support.case.submit")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
